import algoliasearch from 'algoliasearch';
import algoliasearchHelper from 'algoliasearch-helper';
import MicroModal from 'micromodal';

const {registerModule} = window;

class SearchModal {
  constructor(element) {
    this.element = element;
    this.initialize();
  }

  bindEvents() {
    // The synthetic 'showmodal' event is dispatched by external elements — ex.,
    // navigationBar's search button.
    this.element.addEventListener('showmodal', this.handleShowModal.bind(this));

    this.ui.$close.addEventListener('click', this.handleClose.bind(this));
    this.ui.$input.addEventListener('input', this.handleInput.bind(this));
    this.ui.$modalContainer.addEventListener('click', this.handleContainerClick.bind(this));

    // Search events
    this.searchHelper.on('result', this.handleSearchResults.bind(this));
  }

  bindUI() {
    this.ui = {};
    this.ui.$close = this.element.querySelector('[data-ref="close"]');
    this.ui.$input = this.element.querySelector('[data-ref="input"]');
    this.ui.$modalContainer = this.element.querySelector('.modal__container');
    this.ui.$resultsList = this.element.querySelector('[data-ref="results-list"]');
    this.ui.$resultsPlaceholder = this.element.querySelector('[data-ref="results-placeholder"]');
    this.ui.$resultTemplate = this.element.querySelector('[data-ref="result-template"]');
    this.ui.$searchQuery = this.element.querySelector('[data-ref="search-query"]');
    this.ui.$results = this.element.querySelector('[data-ref="results"]');
  }

  /**
   * Maps content types to strings to allow renaming of the content type when displaying it. Prevents having to rename content types in Contentful potentially causing issues.
   *  @param {String} text
   * @return {String}
   */
  getPrettierName(text) {
    const map = {
      webinar: 'Webinars',
      trainingResource: 'Training Resources',
    };
    return (text in map ? map[text] : text);
  }

  /**
   * @param {Object} result
   * @return {Object}
   */
  getResultMetadata(result) {
    let excerpt;

    const metadata = {
      category: result.category,
      title: result.title,
      permalink: result._uri,
      objectID: result.objectID,
    };

    // Posts and webinars
    // Need to add new content types here if you want them to show up
    if (result.contentType === 'post' || result.contentType === 'webinar' || result.contentType == 'trainingResource') {
      excerpt = (result.thumbnailText || result.text || result.description || '').trim();
      metadata.resourceType = this.getPrettierName(result.type);
    }

    // Excerpt
    metadata.excerpt = excerpt.split(' ')
      .slice(0, 30)
      .filter((word) => word) // filter out empty strings
      .join(' ');

    if (excerpt.length > metadata.excerpt.length) {
      metadata.excerpt += '...';
    }

    return metadata;
  }

  handleClose() {
    MicroModal.close(this.modalId);
    this.resetModal();
  }

  handleContainerClick(event) {
    // Prevent clicks within the container from bubbling up, overriding
    // the default behavior which causes the modal to close.
    event.stopPropagation();
  }

  handleInput(event) {
    clearTimeout(this.debounceTimeout);
    this.searchQuery = event.target.value;

    if (this.searchQuery.length > 0) {
      this.debounceTimeout = setTimeout( async () => {
        // Perform the search after debouncing times out
        this.searchHelper.setQuery(this.searchQuery).search();
      }, 500 );
    } else {
      // Clear results if search query is empty
      this.searchResults = [];
      this.renderResults();
    }
  }

  handleSearchResults(event) {
    this.queryID = event.results?.queryID;
    this.searchResults = event.results?.hits || [];
    this.searchResults = this.searchResults.filter((result) => {
      const type = result.type.toLowerCase();
      if (this.types.includes(type)) {
        return result;
      }
    });
    this.renderResults();
  }

  handleShowModal() {
    MicroModal.show(this.modalId, {
      awaitCloseAnimation: true,
      disableScroll: true,
      onShow: () => {
        document.body.classList.add('modal-is-open');
      },
      onClose: () => {
        this.resetModal();
        document.body.classList.remove('modal-is-open');
      },
    });
  }

  initialize() {
    this.bindUI();
    this.setVars();
    this.bindEvents();
    MicroModal.init();
  }

  renderResults() {
    this.ui.$resultsList.replaceChildren();

    this.searchResults.forEach((result, index)=> {
      const element = this.ui.$resultTemplate.content.firstElementChild.cloneNode(true);

      // Populate template elements
      const {category, excerpt, objectID, permalink, resourceType, title} = this.getResultMetadata(result);

      element.querySelector('[data-ref="result-excerpt"]').innerText = excerpt;
      element.querySelector('[data-ref="result-permalink"]').href = permalink;
      element.querySelector('[data-ref="result-title"]').innerHTML = title;
      element.querySelector('[data-ref="result-type"]').innerText = resourceType;

      if (category) {
        element.querySelector('[data-ref="result-category"]').innerText = category;
        if (result.contentType === 'trainingResource' && !result.videoUrl) {
          element.querySelector('[data-ref="result-permalink"]').target = '_blank';
        }
      } else {
        element.querySelector('[data-ref="result-category"]').remove();
      }

      element.dataset.insightsObjectId = objectID;
      element.dataset.insightsPosition = `${index + 1}`;
      element.dataset.insightsQueryId = this.queryID;

      this.ui.$resultsList.appendChild(element);
    });

    this.ui.$searchQuery.innerText = this.searchQuery;

    this.ui.$resultsList.setAttribute('aria-hidden', this.searchQuery.length === 0 || this.searchResults.length === 0);
    this.ui.$resultsPlaceholder.setAttribute('aria-hidden', this.searchQuery.length === 0 || this.searchResults.length > 0);

    if (this.searchResults.length) {
      window.dataLayer.push({event: 'Algolia Hits Viewed'});
    }
  }

  resetModal() {
    // Reset query and results when the modal is closed
    this.ui.$input.value = '';
    this.searchQuery = '';
    this.searchResults = [];
    this.renderResults();
  }

  getIndex() {
    // Change developmentMode = true if you want to use Algolia's QA index.
    const developmentMode = false;
    if (developmentMode) {
      console.warn('#### WARNING ####: Development mode enabled for search.');
      return 'public-qa-en-US';
    }
    // This was changed to a string to prevent issues in other countries that have .lang set differently, when other regions are added it will need to be updated
    return `public-${this.lang}`;
  }

  setVars() {
    this.debounceTimeout = null;
    this.lang = document.documentElement.lang;
    this.modalId = this.element.dataset.modalId;
    this.scope = this.element.getAttribute('data-search-scope');
    this.types = this.scope.split(',');
    this.searchClient = algoliasearch(
      'G0UEB6YYJ8',
      '195525021b740a37c597c6bcde232ffa',
    );
    this.searchHelper = algoliasearchHelper(
      this.searchClient,
      this.getIndex(),
      {clickAnalytics: true,
        disjunctiveFacets: ['type'],
        facets: ['excludeFromResourceCenter', 'excludeFromResourceHub'],
      },
    );
    this.types.forEach((type) => {
      this.searchHelper.addDisjunctiveFacetRefinement('type', type.toLowerCase());
    });
    this.searchHelper.addFacetExclusion('excludeFromResourceCenter', true);
    this.searchHelper.addFacetExclusion('excludeFromResourceHub', true);
    this.searchQuery = '';
    this.searchResults = [];
    this.ui.$results.dataset.insightsIndex = this.getIndex();
  }
}

registerModule('SearchModal', SearchModal);
